import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Anchor,
  DollarSign,
  FileText,
  Image,
  Mail,
  Package,
  Save,
  Search,
  ShoppingCart,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Checkbox } from '@marine/ui/checkbox';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Skeleton } from '@marine/ui/skeleton';
import { Slider } from '@marine/ui/slider';
import { Textarea } from '@marine/ui/textarea';
import { AiModelSelect } from '@marine/ui/AiModelSelect';
import { typedResolver } from '@/lib/typedResolver';
import {
  usePlatformAIModels,
  useRefreshPlatformAIModels,
} from '@/modules/platform/hooks/usePlatformAIModels';
import {
  usePlatformAIPrompts,
  useUpdatePlatformAIPrompt,
  type PlatformAIPrompt,
} from '@/modules/platform/services/platformAiPromptsApi';

// ---------------------------------------------------------------------------
// Module & category config — mirrors the tenant AiPromptEditorTab.tsx so the
// two editors stay comparable at a glance.
// ---------------------------------------------------------------------------

const MODULE_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  inventory: { label: 'Inventory', icon: Anchor },
  crm: { label: 'CRM', icon: ShoppingCart },
  service: { label: 'Service', icon: Package },
};

const MODULE_ORDER = ['inventory', 'crm', 'service'];

const CATEGORY_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  description: { label: 'Description', icon: FileText },
  specs: { label: 'Specs', icon: Search },
  image_analysis: { label: 'Image Analysis', icon: Image },
  comparables: { label: 'Comparables', icon: DollarSign },
  crm: { label: 'Quotes & Email', icon: Mail },
  seo: { label: 'SEO', icon: Search },
  trade_in: { label: 'Trade-In', icon: DollarSign },
};

const CATEGORY_ORDER = [
  'description',
  'specs',
  'image_analysis',
  'comparables',
  'seo',
  'trade_in',
  'crm',
];

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

const promptFormSchema = z.object({
  system_prompt: z.string().min(1, 'System prompt is required'),
  user_prompt_template: z.string().min(1, 'User prompt template is required'),
  model_override: z.string(),
  temperature_override: z.number(),
  max_tokens_override: z.number(),
  override_temperature: z.boolean(),
  override_max_tokens: z.boolean(),
});

type PromptFormValues = z.output<typeof promptFormSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ModuleGroup {
  module: string;
  categories: Record<string, PlatformAIPrompt[]>;
}

function groupByModuleAndCategory(prompts: PlatformAIPrompt[]): ModuleGroup[] {
  const moduleMap: Record<string, Record<string, PlatformAIPrompt[]>> = {};

  for (const p of prompts) {
    const mod = p.module ?? 'inventory';
    const cat = p.metadata?.category ?? 'other';
    if (!moduleMap[mod]) moduleMap[mod] = {};
    if (!moduleMap[mod][cat]) moduleMap[mod][cat] = [];
    moduleMap[mod][cat].push(p);
  }

  for (const mod of Object.keys(moduleMap)) {
    for (const cat of Object.keys(moduleMap[mod])) {
      moduleMap[mod][cat].sort((a, b) => a.sort_order - b.sort_order);
    }
  }

  const result: ModuleGroup[] = [];
  for (const mod of MODULE_ORDER) {
    if (moduleMap[mod]) {
      result.push({ module: mod, categories: moduleMap[mod] });
    }
  }
  for (const mod of Object.keys(moduleMap)) {
    if (!MODULE_ORDER.includes(mod)) {
      result.push({ module: mod, categories: moduleMap[mod] });
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Platform → AI Settings → Prompts tab (#867 D-05).
 *
 * Master/detail editor for the landlord `platform_ai_prompts` DEFAULT
 * templates every dealership inherits until it customizes its own tenant
 * `ai_prompts` row. No "Reset to Default" button here — this IS the default.
 */
export function PromptsTab() {
  const { data: prompts, isLoading: promptsLoading } = usePlatformAIPrompts();
  const updateMutation = useUpdatePlatformAIPrompt();
  const { data: models, isLoading: modelsLoading } = usePlatformAIModels();
  const refreshModelsMutation = useRefreshPlatformAIModels();

  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);

  const form = useForm<PromptFormValues>({
    resolver: typedResolver(promptFormSchema),
    defaultValues: {
      system_prompt: '',
      user_prompt_template: '',
      model_override: '__none__',
      temperature_override: 0.7,
      max_tokens_override: 4096,
      override_temperature: false,
      override_max_tokens: false,
    },
  });

  const promptList = prompts ?? [];
  const moduleGroups = groupByModuleAndCategory(promptList);
  const selectedPrompt = promptList.find((p) => p.id === selectedPromptId) ?? null;

  useEffect(() => {
    if (!selectedPrompt) return;
    form.reset({
      system_prompt: selectedPrompt.system_prompt,
      user_prompt_template: selectedPrompt.user_prompt_template,
      model_override: selectedPrompt.model_override ?? '__none__',
      temperature_override: selectedPrompt.temperature_override ?? 0.7,
      max_tokens_override: selectedPrompt.max_tokens_override ?? 4096,
      override_temperature: selectedPrompt.temperature_override !== null,
      override_max_tokens: selectedPrompt.max_tokens_override !== null,
    });
  }, [selectedPromptId, selectedPrompt, form]);

  useEffect(() => {
    if (!selectedPromptId && promptList.length > 0) {
      setSelectedPromptId(promptList[0].id);
    }
  }, [promptList, selectedPromptId]);

  const handleSave = (values: PromptFormValues) => {
    if (!selectedPromptId) return;

    updateMutation.mutate(
      {
        id: selectedPromptId,
        input: {
          system_prompt: values.system_prompt,
          user_prompt_template: values.user_prompt_template,
          model_override: values.model_override === '__none__' ? null : values.model_override,
          temperature_override: values.override_temperature ? values.temperature_override : null,
          max_tokens_override: values.override_max_tokens ? values.max_tokens_override : null,
        },
      },
      {
        onSuccess: () => toast.success('Default prompt updated'),
        onError: () => toast.error('Failed to update default prompt'),
      },
    );
  };

  const isSaving = updateMutation.isPending;

  // ---------------------------------------------------------------------
  // Render: Sidebar
  // ---------------------------------------------------------------------

  const renderSidebar = () => (
    <div className="space-y-5">
      {promptsLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-7 w-full" />
          ))}
        </div>
      ) : (
        moduleGroups.map((group) => {
          const modConfig = MODULE_LABELS[group.module];
          const ModIcon = modConfig?.icon ?? FileText;
          const modLabel = modConfig?.label ?? group.module;

          const catKeys = CATEGORY_ORDER.filter((cat) => group.categories[cat]?.length);
          for (const cat of Object.keys(group.categories)) {
            if (!catKeys.includes(cat)) catKeys.push(cat);
          }

          return (
            <div key={group.module} className="space-y-3">
              <div className="flex items-center gap-1.5 px-2">
                <ModIcon className="size-3.5 text-muted-foreground" />
                <span className="text-2xs font-bold uppercase tracking-wider text-muted-foreground">
                  {modLabel}
                </span>
              </div>

              {catKeys.map((cat) => {
                const catConfig = CATEGORY_LABELS[cat];
                const label = catConfig?.label ?? cat;
                const catPrompts = group.categories[cat] ?? [];

                return (
                  <div key={cat} className="space-y-0.5">
                    <div className="px-2 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                        {label}
                      </span>
                    </div>
                    {catPrompts.map((prompt) => (
                      <button
                        key={prompt.id}
                        type="button"
                        onClick={() => setSelectedPromptId(prompt.id)}
                        title={prompt.is_active ? undefined : 'Inactive — toggle on to use this prompt'}
                        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent ${
                          selectedPromptId === prompt.id ? 'bg-accent font-medium' : ''
                        } ${prompt.is_active ? '' : 'text-muted-foreground'}`}
                      >
                        <span className="flex-1 truncate">{prompt.name}</span>
                        {!prompt.is_active && (
                          <span className="shrink-0 rounded-sm bg-muted px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            Off
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );

  // ---------------------------------------------------------------------
  // Render: Detail Panel
  // ---------------------------------------------------------------------

  const renderDetailPanel = () => {
    if (!selectedPrompt) {
      return (
        <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground">Select a prompt template to edit</p>
        </div>
      );
    }

    const watchTemp = form.watch('temperature_override');
    const overrideTemp = form.watch('override_temperature');
    const overrideMaxTokens = form.watch('override_max_tokens');

    const modConfig = MODULE_LABELS[selectedPrompt.module ?? 'inventory'];

    return (
      <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{selectedPrompt.name}</h3>
            {modConfig && (
              <Badge variant="outline" size="sm">
                {modConfig.label}
              </Badge>
            )}
            {!selectedPrompt.is_active && (
              <Badge variant="secondary" size="sm">
                Inactive
              </Badge>
            )}
          </div>
          {selectedPrompt.description && (
            <p className="text-2sm text-muted-foreground">{selectedPrompt.description}</p>
          )}
          <p className="text-2xs text-muted-foreground">
            Edits apply to every dealership that has not customized this template. Changes reach
            tenant apps within ~5 minutes (cached read), no redeploy.
          </p>
        </div>

        {/* System Prompt */}
        <div className="space-y-1.5">
          <Label htmlFor="system_prompt">System Prompt</Label>
          <Textarea
            id="system_prompt"
            rows={6}
            className="font-mono text-xs"
            {...form.register('system_prompt')}
          />
          {form.formState.errors.system_prompt && (
            <p className="text-sm text-destructive" role="alert">
              {form.formState.errors.system_prompt.message}
            </p>
          )}
        </div>

        {/* User Prompt Template */}
        <div className="space-y-1.5">
          <Label htmlFor="user_prompt_template">User Prompt Template</Label>
          <Textarea
            id="user_prompt_template"
            rows={8}
            className="font-mono text-xs"
            {...form.register('user_prompt_template')}
          />
          {form.formState.errors.user_prompt_template && (
            <p className="text-sm text-destructive" role="alert">
              {form.formState.errors.user_prompt_template.message}
            </p>
          )}
        </div>

        {/* Variable Reference Panel */}
        {selectedPrompt.variables && selectedPrompt.variables.length > 0 && (
          <div className="space-y-2 rounded-lg border border-border p-4">
            <div className="space-y-0.5">
              <h4 className="text-xs font-semibold">Available Variables</h4>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selectedPrompt.variables.map((variable) => (
                <Badge key={variable} variant="outline">
                  {`{{${variable}}}`}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Per-template Overrides */}
        <div className="space-y-4 rounded-lg border border-border p-4">
          <h4 className="text-xs font-semibold">Per-template Overrides</h4>

          <AiModelSelect
            label="Model Override"
            value={form.watch('model_override') === '__none__' ? null : form.watch('model_override')}
            onChange={(v) => form.setValue('model_override', v, { shouldDirty: true })}
            models={models}
            isLoading={modelsLoading}
            onRefresh={() => refreshModelsMutation.mutate()}
            isRefreshing={refreshModelsMutation.isPending}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-2xs"
              onClick={() => form.setValue('model_override', '__none__', { shouldDirty: true })}
            >
              Use default model
            </Button>
          </div>

          {/* Temperature Override */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="override_temperature"
                checked={overrideTemp}
                onCheckedChange={(checked) =>
                  form.setValue('override_temperature', Boolean(checked), { shouldDirty: true })
                }
              />
              <Label htmlFor="override_temperature" className="text-xs">
                Override temperature
              </Label>
              {overrideTemp && (
                <span className="ml-auto text-xs font-mono text-muted-foreground">
                  {Number.isFinite(watchTemp) ? watchTemp.toFixed(1) : '0.7'}
                </span>
              )}
            </div>
            {overrideTemp && (
              <Slider
                min={0}
                max={2}
                step={0.1}
                value={[watchTemp]}
                onValueChange={(value) =>
                  form.setValue('temperature_override', value[0], { shouldDirty: true })
                }
              />
            )}
          </div>

          {/* Max Tokens Override */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="override_max_tokens"
                checked={overrideMaxTokens}
                onCheckedChange={(checked) =>
                  form.setValue('override_max_tokens', Boolean(checked), { shouldDirty: true })
                }
              />
              <Label htmlFor="override_max_tokens" className="text-xs">
                Override max tokens
              </Label>
            </div>
            {overrideMaxTokens && (
              <Input
                type="number"
                min={1}
                max={100000}
                value={form.watch('max_tokens_override')}
                onChange={(e) =>
                  form.setValue('max_tokens_override', Number(e.target.value), { shouldDirty: true })
                }
                className="w-40"
              />
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={isSaving || !form.formState.isDirty}>
            <Save className="me-1.5 size-3.5" />
            Save Changes
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      <div className="rounded-lg border border-border p-4">{renderSidebar()}</div>
      <div>{renderDetailPanel()}</div>
    </div>
  );
}
